import {
  adminLeadListPageSizes,
  leadSourceKinds,
  type AdminLeadListPage,
  type AdminLeadListItem,
  type LeadPipelineStage,
  type LeadSourceKind,
} from "@whitehaven/contracts";
import type { AuthorizationSubject } from "@whitehaven/permissions";
import { canAccessAdminCrm } from "./admin-crm-authorization";
import { isAdminLeadSummaryConsistent } from "./admin-lead-detail-model";
import { isBoundedOpaqueCrmCursor } from "./opaque-cursor-model";
import { leadPipelineStageOrder } from "./pipeline-stage-model";

export { leadPipelineStageLabels } from "./pipeline-stage-model";

export const ALL_LEAD_PIPELINE_STAGES = "ALL" as const;
export const ALL_LEAD_SOURCE_KINDS = "ALL" as const;
export const UNRECORDED_LEAD_SOURCE = "UNRECORDED" as const;
export const ALL_LEAD_ASSIGNMENTS = "ALL" as const;
export const ASSIGNED_LEADS = "ASSIGNED" as const;
export const UNASSIGNED_LEADS = "UNASSIGNED" as const;

export type AdminLeadListStageFilter =
  | typeof ALL_LEAD_PIPELINE_STAGES
  | LeadPipelineStage;
export type AdminLeadListSourceFilter =
  | typeof ALL_LEAD_SOURCE_KINDS
  | typeof UNRECORDED_LEAD_SOURCE
  | LeadSourceKind;
export type AdminLeadListSort = "UPDATED_DESC" | "CREATED_DESC" | "NAME_ASC";
export type AdminLeadListAssignmentFilter =
  | typeof ALL_LEAD_ASSIGNMENTS
  | typeof ASSIGNED_LEADS
  | typeof UNASSIGNED_LEADS;

export interface AdminLeadListFilters {
  readonly pipelineStage: AdminLeadListStageFilter;
  readonly sourceKind: AdminLeadListSourceFilter;
  readonly assignment: AdminLeadListAssignmentFilter;
  readonly sort: AdminLeadListSort;
}

export const defaultAdminLeadListFilters: AdminLeadListFilters = Object.freeze({
  pipelineStage: ALL_LEAD_PIPELINE_STAGES,
  sourceKind: ALL_LEAD_SOURCE_KINDS,
  assignment: ALL_LEAD_ASSIGNMENTS,
  sort: "UPDATED_DESC",
});

export const leadSourceKindLabels = {
  DIRECT: "Direct",
  ORGANIC_SEARCH: "Organic search",
  REFERRAL: "Referral",
  CAMPAIGN: "Campaign",
  CHATGPT: "ChatGPT",
  UNKNOWN: "Unknown",
} as const satisfies Readonly<Record<LeadSourceKind, string>>;

export const adminLeadListStageOptions = leadPipelineStageOrder;
export const adminLeadListSourceOptions = leadSourceKinds;

export function canViewAdminLeadList(
  subject: AuthorizationSubject | null,
): boolean {
  return canAccessAdminCrm(subject);
}

export function hasActiveAdminLeadListFilters(
  filters: AdminLeadListFilters,
): boolean {
  return (
    filters.pipelineStage !== ALL_LEAD_PIPELINE_STAGES ||
    filters.sourceKind !== ALL_LEAD_SOURCE_KINDS ||
    filters.assignment !== ALL_LEAD_ASSIGNMENTS
  );
}

function timestampValue(value: string): number {
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function compareLeadIds(left: AdminLeadListItem, right: AdminLeadListItem) {
  return left.leadId.localeCompare(right.leadId, "en");
}

function compareAdminLeadListItems(
  left: AdminLeadListItem,
  right: AdminLeadListItem,
  sort: AdminLeadListSort,
): number {
  if (sort === "NAME_ASC") {
    return (
      left.displayName.localeCompare(right.displayName, "en", {
        sensitivity: "base",
      }) || compareLeadIds(left, right)
    );
  }

  const field = sort === "CREATED_DESC" ? "createdAt" : "updatedAt";
  return (
    timestampValue(right[field]) - timestampValue(left[field]) ||
    compareLeadIds(left, right)
  );
}

export function selectAdminLeadListItems(
  items: readonly AdminLeadListItem[],
  filters: AdminLeadListFilters,
): readonly AdminLeadListItem[] {
  return items
    .filter((item) => {
      const stageMatches =
        filters.pipelineStage === ALL_LEAD_PIPELINE_STAGES ||
        item.pipelineStage === filters.pipelineStage;
      const sourceMatches =
        filters.sourceKind === ALL_LEAD_SOURCE_KINDS ||
        (filters.sourceKind === UNRECORDED_LEAD_SOURCE
          ? item.sourceKind === null
          : item.sourceKind === filters.sourceKind);
      const assignmentMatches =
        filters.assignment === ALL_LEAD_ASSIGNMENTS ||
        (filters.assignment === ASSIGNED_LEADS
          ? item.assignedUserId !== null
          : item.assignedUserId === null);

      return stageMatches && sourceMatches && assignmentMatches;
    })
    .sort((left, right) =>
      compareAdminLeadListItems(left, right, filters.sort),
    );
}

export function mergeAdminLeadListItems(
  current: readonly AdminLeadListItem[],
  incoming: readonly AdminLeadListItem[],
): readonly AdminLeadListItem[] {
  const merged = [...current];
  const knownLeadIds = new Set(current.map(({ leadId }) => leadId));

  for (const item of incoming) {
    if (knownLeadIds.has(item.leadId)) continue;
    knownLeadIds.add(item.leadId);
    merged.push(item);
  }

  return merged;
}

export function isAdminLeadListPageConsistent(
  value: unknown,
): value is AdminLeadListPage {
  if (typeof value !== "object" || value === null) return false;
  const page = value as AdminLeadListPage;
  if (
    !Array.isArray(page.items) ||
    page.items.length > adminLeadListPageSizes[0] ||
    (page.items.length === 0 && page.nextCursor !== null) ||
    (page.nextCursor !== null && !isBoundedOpaqueCrmCursor(page.nextCursor))
  ) {
    return false;
  }

  const leadIds = new Set<string>();
  return page.items.every((item) => {
    if (
      !isAdminLeadSummaryConsistent(item) ||
      item.leadRecordStatus !== "ACTIVE" ||
      leadIds.has(item.leadId)
    ) {
      return false;
    }
    leadIds.add(item.leadId);
    return true;
  });
}
